import React from 'react';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { Button } from './Button';
import { Badge } from './Badge';

/**
 * Certificate — Oblique Design System Component
 * Renders an official verified digital certificate of completion.
 */
export function Certificate({
  traineeName = 'Scale Agency Specialist',
  courseTitle = 'The Scale Agency: Product, SEO & Sales Blueprint',
  score = 90,
  credentialId = 'SCALE-2026-CERT',
  issueDate = new Date().toLocaleDateString('en-US', { dateStyle: 'long' }),
  issuerName = 'The Scale Agency Executive Team',
  onPrint,
  onClose,
  className = ''
}) {
  return (
    <div className={`bg-[var(--bg-surface)] text-[var(--text-primary)] border-8 border-[var(--border)] rounded-[24px] p-8 sm:p-12 shadow-2xl relative text-center max-w-2xl w-full mx-auto ${className}`}>
      {/* Verified Seal */}
      <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-amber-500 to-amber-600 flex items-center justify-center text-white mx-auto mb-4 shadow-lg shadow-amber-500/25">
        <ObliqueIcon name="verified" size={36} />
      </div>

      <div className="uppercase tracking-widest text-[11px] font-bold text-[var(--accent)] mb-1 font-mono">
        The Scale Agency Academy
      </div>
      <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--text-primary)] mb-1">
        Certificate of Completion
      </h2>
      <p className="text-[11px] uppercase tracking-wider text-[var(--text-tertiary)] mb-6 font-mono">
        Official Credential • ID #{credentialId.toUpperCase()}
      </p>

      <p className="text-[13px] text-[var(--text-secondary)] mb-1.5">This certifies that</p>
      <div className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] underline decoration-[var(--accent)] decoration-2 underline-offset-8 mb-6">
        {traineeName}
      </div>

      <p className="text-[13px] text-[var(--text-secondary)] max-w-lg mx-auto leading-relaxed mb-8">
        has successfully passed all examinations with a verified grade of <strong>{score}%</strong> and demonstrated mastery across 
        <strong> {courseTitle}</strong>, including Commercial Packages, 5-Pillar Omnichannel Search (SEO, GEO, AEO, Local, SXO), and Diagnostic Discovery Sales.
      </p>

      {/* Signature & Date Footer */}
      <div className="flex justify-between items-end pt-6 border-t border-[var(--border)] text-left text-xs">
        <div>
          <div className="font-semibold text-[var(--text-primary)]">Issue Date:</div>
          <div className="text-[var(--text-tertiary)] text-[12px]">{issueDate}</div>
        </div>
        <div className="text-right">
          <Badge variant="success" size="sm" dot>Verified Record</Badge>
          <div className="font-semibold text-[var(--text-primary)] mt-1">{issuerName}</div>
        </div>
      </div>

      {/* Print / Action Buttons */}
      <div className="mt-8 flex justify-center gap-3">
        <Button 
          variant="primary" 
          onClick={onPrint || (() => window.print())}
          iconLeft={<ObliqueIcon name="print" size={16} />}
        >
          Print / Save PDF
        </Button>
        {onClose && (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        )}
      </div>
    </div>
  );
}

export default Certificate;
